import { useCallback, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { Button } from '../Button/Button';
import './TruncatedText.css';

export type ExpandableTextProps = {
  text?: string;
  /**
   * Rich inline content clamped instead of `text` (e.g. a prompt template
   * with token pills). Ignored when `text` is provided.
   */
  children?: ReactNode;
  /** Collapsed line clamp. Figma Data Request uses ~3 lines. */
  maxLines?: number;
  /**
   * Keep the text's own line breaks (white-space: pre-wrap) — e.g. inherited
   * prompt previews that must render exactly like their source textarea.
   */
  preserveWhitespace?: boolean;
  /** Body text color. Defaults to secondary (Figma Data Request rows). */
  color?: 'primary' | 'secondary';
  className?: string;
};

/**
 * Multiline text that clamps to `maxLines` and shows Spark ghost "Show more"
 * / "Show less" only when the content actually overflows.
 */
export function ExpandableText({
  text = '',
  children,
  maxLines = 3,
  preserveWhitespace = false,
  color = 'secondary',
  className = '',
}: ExpandableTextProps) {
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const bodyRef = useRef<HTMLParagraphElement>(null);

  const measure = useCallback(() => {
    if (expanded) return;
    const el = bodyRef.current;
    if (!el) return;
    setOverflows(el.scrollHeight > el.clientHeight + 1);
  }, [expanded]);

  useLayoutEffect(() => {
    measure();
    const el = bodyRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() => {
      requestAnimationFrame(measure);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [text, children, maxLines, measure]);

  if (!text && children == null) return null;

  return (
    <div className={['spark-expandable-text', className].filter(Boolean).join(' ')}>
      <p
        ref={bodyRef}
        className={[
          'spark-expandable-text-body',
          expanded ? '' : 'spark-expandable-text-body--clamped',
          preserveWhitespace ? 'spark-expandable-text-body--pre-wrap' : '',
          color === 'primary' ? 'spark-expandable-text-body--primary' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        style={{ ['--spark-expandable-lines' as string]: String(maxLines) }}
      >
        {text || children}
      </p>
      {overflows ? (
        <Button
          type="button"
          variant="ghost"
          size="tiny"
          onClick={() => setExpanded((value) => !value)}
          aria-expanded={expanded}
        >
          {expanded ? 'Show less' : 'Show more'}
        </Button>
      ) : null}
    </div>
  );
}
